import {
    Avatar,
    Box,
    Button,
    Chip,
    Divider,
    Icon,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { Link, matchPath } from "react-router-dom";
import { SubscriptionTypesDefintions } from "../../constants/SubscriptionConstants";
import { UserContext } from "../../contexts/UserContext";
import usePageSize from "../../hooks/UsePageSize";
import { RootState } from "../../slices/Store";
import { getAuthManager } from "../../utils/auth";
import { getAccountExpirationDateDiff } from "../../utils/date";
import { getUserMenuItemsForSize } from "../../utils/menu";

export default function UserMenu() {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const user = useContext(UserContext);

    const accountDetail = useSelector(
        (state: RootState) => state.shared.accountDetail
    );

    const size = usePageSize();

    const drawerItems = getUserMenuItemsForSize(size);

    const toggleMode = () => {
        const mode = theme.palette.mode === "dark" ? "light" : "dark";
        localStorage.setItem("theme", mode);
        window.location.reload();
    };

    const handleLogout = async () => {
        const authenticator = getAuthManager();
        await authenticator.logout();
    };

    const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const subscriptionType = SubscriptionTypesDefintions.find(
        (d) => d.type === accountDetail.subscriptionType
    );

    return (
        <Box>
            <Button
                endIcon={<Icon>keyboard_arrow_down</Icon>}
                onClick={handleMenu}
                size="small"
                color={"primary"}
                variant={"outlined"}
            >
                {user.displayName}
            </Button>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
            >
                <Stack
                    divider={<Divider />}
                    sx={{ maxWidth: "275px" }}
                >
                    <Stack
                        direction="row"
                        sx={{ p: 2 }}
                        gap={2}
                    >
                        <Avatar
                            src={accountDetail.currentUser.avatarUrl}
                            sx={{ width: 50, height: 50, margin: "auto" }}
                        />

                        <Stack
                            justifyContent="space-around"
                            alignItems="start"
                        >
                            <Typography color="primary">
                                {accountDetail.currentUser.displayName}
                            </Typography>

                            {subscriptionType && (
                                <Chip
                                    size="small"
                                    color="primary"
                                    label={subscriptionType.title}
                                />
                            )}
                        </Stack>
                    </Stack>

                    <Box sx={{ p: 2 }}>
                        {accountDetail.isExpired ? (
                            <Typography color="error">
                                اشتراک شما پایان یافته
                            </Typography>
                        ) : (
                            <Typography
                                fontSize="0.71rem"
                                color="text.secondary"
                            >
                                {getAccountExpirationDateDiff(
                                    accountDetail.endDate
                                )}
                            </Typography>
                        )}
                    </Box>

                    {drawerItems.map((di) => {
                        const urlMatch = matchPath(
                            window.location.pathname,
                            di.url
                        );

                        return (
                            <Link
                                key={di.url}
                                to={di.url}
                                style={{ textDecoration: "none" }}
                            >
                                <MenuItem
                                    onClick={handleClose}
                                    sx={{ py: 1.5 }}
                                >
                                    <ListItemIcon>
                                        <Icon
                                            color={
                                                urlMatch
                                                    ? "primary"
                                                    : "disabled"
                                            }
                                        >
                                            {di.icon}
                                        </Icon>
                                    </ListItemIcon>
                                    <ListItemText
                                        sx={{ color: "text.primary" }}
                                    >
                                        {di.title}
                                    </ListItemText>
                                </MenuItem>
                            </Link>
                        );
                    })}

                    <Box>
                        <MenuItem
                            onClick={toggleMode}
                            sx={{ py: 1.5 }}
                        >
                            <ListItemIcon>
                                <Icon color="disabled">
                                    {theme.palette.mode === "light"
                                        ? "dark_mode"
                                        : "light_mode"}
                                </Icon>
                            </ListItemIcon>
                            <ListItemText color="text.primary">
                                {theme.palette.mode === "light"
                                    ? "حالت شب"
                                    : "حالت روز"}
                            </ListItemText>
                        </MenuItem>
                    </Box>

                    <Box>
                        <MenuItem
                            onClick={handleLogout}
                            sx={{ py: 1.5 }}
                        >
                            <ListItemIcon>
                                <Icon color="disabled">logout</Icon>
                            </ListItemIcon>
                            <ListItemText color="text.primary">
                                خروج از حساب کاربری
                            </ListItemText>
                        </MenuItem>
                    </Box>
                </Stack>
            </Menu>
        </Box>
    );
}
